import { SelectionUtils } from "@/utils/SelectionUtils";
import { useNode } from "@craftjs/core";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TableChartIcon from "@mui/icons-material/TableChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Divider, ListItemIcon, Menu, MenuItem } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import { useEffect, useRef, useState } from "react";
import StyledToggleButtonGroup from "../../../components/Viewport/components/Toolbar/components/StyledToggleButtonGroup/StyledToggleButtonGroup";
import TextToolbar from "./TextToolbar";

export default function CustomizedDividers() {
    const [enabled, setEnabled] = useState<boolean>(false);

    const tableOptionsRef = useRef<HTMLButtonElement | null>(null);
    const [showTableOptions, setShowTableOptions] = useState<boolean>(false);

    const selectionUtils = useRef<SelectionUtils>(new SelectionUtils());

    const {
        actions: { setProp },
    } = useNode();

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        document.execCommand("styleWithCSS", false, true as any);
        document.addEventListener("selectionchange", onSelectionChange, true);

        return () => {
            document.removeEventListener("selectionchange", onSelectionChange, true);
        };
    }, []);

    const onSelectionChange = () => {
        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const baseNode = activeSelection.anchorNode as HTMLElement;
        if (!baseNode) return;

        const baseNodeParent = baseNode.parentElement as HTMLElement;
        if (
            (baseNode.closest && baseNode.closest(".craftjs-toolbar")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".craftjs-toolbar")) ||
            (baseNode.closest && baseNode.closest(".chrome-picker")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".chrome-picker")) ||
            (baseNode.closest && baseNode.closest(".link-popper")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".link-popper"))
        ) {
            return;
        }

        setEnabled(true);

        if ((activeSelection.type === "Range" || activeSelection.type === "Caret") && activeSelection.rangeCount > 0) {
            selectionUtils.current.save();
        } else if (activeSelection.type) {
            selectionUtils.current.clearSaved();
        }
    };

    const insertRow = (below = false) => {
        setShowTableOptions(false);
        selectionUtils.current.restore();
        let cellTag = selectionUtils.current.findParentTag("TH") as HTMLTableCellElement | null;
        if (!cellTag) cellTag = selectionUtils.current.findParentTag("TD") as HTMLTableCellElement | null;
        if (!cellTag) return;

        const rowId = cellTag.dataset.rowId;
        if (!rowId) return;
        let rowNumber = parseInt(rowId);
        if (below) rowNumber++;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            const content = props.content as Array<Array<string>>;
            const columnCount = content[0].length;
            const emptyRow = Array(columnCount).fill("");
            const newContent = [...content.slice(0, rowNumber), emptyRow, ...content.slice(rowNumber)];

            props.content = newContent;
        }, 500);
    };

    const removeRow = () => {
        setShowTableOptions(false);
        selectionUtils.current.restore();
        let cellTag = selectionUtils.current.findParentTag("TH") as HTMLTableCellElement | null;
        if (!cellTag) cellTag = selectionUtils.current.findParentTag("TD") as HTMLTableCellElement | null;
        if (!cellTag) return;

        const rowId = cellTag.dataset.rowId;
        if (!rowId) return;
        const rowNumber = parseInt(rowId);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            const content = props.content as Array<Array<string>>;
            const columnCount = content[0].length;
            const emptyRow = Array(columnCount).fill("");
            let newContent = [...content.slice(0, rowNumber), ...content.slice(rowNumber + 1)];
            if (newContent.length === 0) newContent = [emptyRow];
            props.content = newContent;
        }, 500);
    };

    const insertColumn = (after = false) => {
        setShowTableOptions(false);
        selectionUtils.current.restore();
        let cellTag = selectionUtils.current.findParentTag("TH") as HTMLTableCellElement | null;
        if (!cellTag) cellTag = selectionUtils.current.findParentTag("TD") as HTMLTableCellElement | null;
        if (!cellTag) return;

        const columnId = cellTag.dataset.columnId;
        if (!columnId) return;
        let columnNumber = parseInt(columnId);
        if (after) columnNumber++;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            const content = props.content as Array<Array<string>>;
            const newContent: string[][] = [];

            for (let i = 0; i < content.length; i++) {
                const row = content[i];
                const newRow = [...row.slice(0, columnNumber), "", ...row.slice(columnNumber)];
                newContent.push(newRow);
            }

            props.content = newContent;
        }, 500);
    };

    const removeColumn = () => {
        setShowTableOptions(false);
        selectionUtils.current.restore();
        let cellTag = selectionUtils.current.findParentTag("TH") as HTMLTableCellElement | null;
        if (!cellTag) cellTag = selectionUtils.current.findParentTag("TD") as HTMLTableCellElement | null;
        if (!cellTag) return;

        const columnId = cellTag.dataset.columnId;
        if (!columnId) return;
        const columnNumber = parseInt(columnId);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            const content = props.content as Array<Array<string>>;
            const newContent: string[][] = [];

            for (let i = 0; i < content.length; i++) {
                const row = content[i];
                let newRow = [...row.slice(0, columnNumber), ...row.slice(columnNumber + 1)];
                if (newRow.length === 0) newRow = [""];
                newContent.push(newRow);
            }

            props.content = newContent;
        }, 500);
    };

    const setWidth = () => {
        setShowTableOptions(false);
        selectionUtils.current.restore();
        let cellTag = selectionUtils.current.findParentTag("TH") as HTMLTableCellElement | null;
        if (!cellTag) cellTag = selectionUtils.current.findParentTag("TD") as HTMLTableCellElement | null;
        if (!cellTag) return;

        const columnId = cellTag.dataset.columnId;
        if (!columnId) return;

        const newWidthResponse = prompt("Enter new column width in pixels:", "250");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setProp((props: any) => {
            // update columnWidths by assigning a new dictionary with the updated value
            if (!newWidthResponse) props.columnWidths = { ...props.columnWidths, [columnId]: null };
            else {
                const newWidth = parseInt(newWidthResponse);
                props.columnWidths = { ...props.columnWidths, [columnId]: newWidth };
            }
        }, 500);
    };

    return (
        <>
            <StyledToggleButtonGroup
                disabled={!enabled}
                size="small"
                value={"table"}
                exclusive
                aria-label="table options"
            >
                <ToggleButton
                    value="dropdown"
                    aria-label="table options"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setShowTableOptions(true);
                        selectionUtils.current.restore();
                    }}
                    component="button"
                    ref={tableOptionsRef}
                >
                    <TableChartIcon />
                    <ArrowDropDownIcon />
                </ToggleButton>
            </StyledToggleButtonGroup>

            <Menu
                anchorEl={tableOptionsRef.current}
                open={showTableOptions}
                onClose={() => setShowTableOptions(false)}
                PaperProps={{
                    style: {
                        width: "20ch",
                    },
                }}
            >
                <MenuItem dense onClick={() => insertRow()}>
                    <ListItemIcon>
                        <TableRowsIcon />
                    </ListItemIcon>
                    Insert row above
                </MenuItem>
                <MenuItem dense onClick={() => insertRow(true)}>
                    <ListItemIcon>
                        <TableRowsIcon />
                    </ListItemIcon>
                    Insert row below
                </MenuItem>
                <MenuItem dense onClick={() => removeRow()}>
                    <ListItemIcon>
                        <TableRowsIcon />
                    </ListItemIcon>
                    Remove row
                </MenuItem>
                <Divider />
                <MenuItem dense onClick={() => insertColumn()}>
                    <ListItemIcon>
                        <ViewColumnIcon />
                    </ListItemIcon>
                    Insert column before
                </MenuItem>
                <MenuItem dense onClick={() => insertColumn(true)}>
                    <ListItemIcon>
                        <ViewColumnIcon />
                    </ListItemIcon>
                    Insert column after
                </MenuItem>
                <MenuItem dense onClick={() => removeColumn()}>
                    <ListItemIcon>
                        <ViewColumnIcon />
                    </ListItemIcon>
                    Remove column
                </MenuItem>
                <Divider />
                <MenuItem dense onClick={() => setWidth()}>
                    <ListItemIcon>
                        <ViewColumnIcon />
                    </ListItemIcon>
                    Set column width
                </MenuItem>
            </Menu>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

            <TextToolbar />
        </>
    );
}
